import { useEffect, useRef } from "react";

import { useActivityEvents } from "../api/hooks";
import { useNotificationStore } from "../store/notificationStore";
import { useSettingsStore } from "../store/settingsStore";
import { useAuthStore } from "../store/authStore";

export function JobNotificationsWatcher() {
  const activityRefreshMs = useSettingsStore((state) => state.activityRefreshMs);
  const authRequired = useAuthStore((state) => state.authRequired);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const enabled = authRequired === false || (authRequired === true && isAuthenticated);
  const { data } = useActivityEvents(100, activityRefreshMs, enabled);
  const enqueue = useNotificationStore((state) => state.enqueue);
  const initialized = useRef(false);
  const lastTimestamp = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const events = data?.events ?? [];
    if (events.length === 0) {
      return;
    }

    if (!initialized.current) {
      initialized.current = true;
      lastTimestamp.current = events[events.length - 1]?.timestamp ?? null;
      return;
    }

    const newEvents = events.filter((event) => {
      if (!lastTimestamp.current) {
        return true;
      }
      return event.timestamp > lastTimestamp.current;
    });

    if (newEvents.length === 0) {
      return;
    }

    newEvents.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
    lastTimestamp.current = newEvents[newEvents.length - 1]?.timestamp ?? lastTimestamp.current;

    newEvents.forEach((event) => {
      if (event.type === "job_failed") {
        enqueue({
          message: event.error ? `Archivio fallito: ${event.error}` : "Archivio fallito",
          severity: "error"
        });
      } else if (event.type === "job_completed") {
        const report = event.report as Record<string, unknown> | undefined;
        const accountsProcessed = Number(report?.accounts_processed ?? 0);
        const accountsWithErrors = Number(report?.accounts_with_errors ?? 0);
        const message = accountsWithErrors > 0
          ? `Archivio completato con avvisi: ${accountsProcessed} caselle, ${accountsWithErrors} con errori`
          : `Archivio completato: ${accountsProcessed} caselle senza errori`;
        enqueue({
          message,
          severity: accountsWithErrors > 0 ? "warning" : "success"
        });
      } else if (event.type === "account_progress" && event.status === "failed") {
        const account = event.account ? ` (${event.account})` : "";
        enqueue({
          message: event.error ? `Errore su casella${account}: ${event.error}` : `Errore su casella${account}`,
          severity: "error"
        });
      } else if (event.type === "account_progress" && event.status === "completed") {
        const account = event.account ? ` (${event.account})` : "";
        enqueue({
          message: `Casella completata${account}`,
          severity: "success",
          autoHideDuration: 3500
        });
      }
    });
  }, [data, enabled, enqueue]);

  return null;
}
